// app.secrets — read/write project secrets at `secrets/{name}` with
// SSE-KMS encryption at rest (one platform-wide CMK; the key policy
// delegates to IAM, and only the project's runtime Lambda role holds
// kms:Decrypt, scoped to its own bucket by encryption context) — so
// even if a misconfiguration leaked s3:GetObject on secrets/* to a
// user, they couldn't decrypt the ciphertext without the key. Defense
// in depth on top of the per-prefix IAM gate.
//
// The SDK is transparent: writes pass through SSE-KMS metadata, reads
// rely on KMS auto-decrypting at S3's edge.

import {
  GetObjectCommand,
  ListObjectsV2Command,
  PutObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";

export interface SecretsConfig {
  getClient: () => Promise<S3Client>;
  bucket: string;
  /** Slash-terminated prefix for secrets — typically `"secrets/"`. */
  secretsPrefix: string;
  /** KMS CMK arn (from s3p.project.json `server.kmsKeyArn`). When set,
   *  puts include SSE-KMS metadata so S3 stores ciphertext. */
  kmsKeyArn?: string;
}

/** A secret's existence + metadata — never its value. */
export interface SecretEntry {
  name: string;
  size: number;
  lastModified?: string;
}

export interface SecretsModule {
  /** Get a secret's plaintext value. Returns null if missing. */
  get(name: string): Promise<string | null>;
  /** Get a secret's plaintext value or throw with a clear error. */
  getRequired(name: string): Promise<string>;
  /** Write a secret. Server-only — IAM gates this; KMS gates decryption. */
  put(name: string, value: string): Promise<void>;
  /** Enumerate WHICH secrets exist (names + metadata) — never their values.
   *  Listing needs only s3:ListBucket on the prefix; decryption stays gated
   *  by KMS regardless. The console's Secrets panel is built on this. */
  list(): Promise<SecretEntry[]>;
}

const VALID_NAME = /^[a-z0-9][a-z0-9-_.]*$/i;

export function createSecrets(cfg: SecretsConfig): SecretsModule {
  function checkName(name: string): void {
    if (!VALID_NAME.test(name)) {
      throw new Error(`app.secrets: invalid secret name '${name}'`);
    }
  }

  async function getOpt(name: string): Promise<string | null> {
    checkName(name);
    const s3 = await cfg.getClient();
    try {
      const res = await s3.send(
        new GetObjectCommand({ Bucket: cfg.bucket, Key: `${cfg.secretsPrefix}${name}` }),
      );
      const bytes = await (
        res.Body as { transformToByteArray: () => Promise<Uint8Array> }
      ).transformToByteArray();
      return new TextDecoder().decode(bytes);
    } catch (e) {
      const status = (e as { $metadata?: { httpStatusCode?: number } })?.$metadata?.httpStatusCode;
      const code = (e as { name?: string })?.name;
      if (status === 404 || code === "NoSuchKey" || code === "NotFound") return null;
      throw e;
    }
  }

  return {
    get: getOpt,
    async getRequired(name: string) {
      const v = await getOpt(name);
      if (v == null) {
        throw new Error(
          `app.secrets.getRequired('${name}'): secret missing at ${cfg.secretsPrefix}${name}. ` +
            `Set via app.secrets.put(name, value) from a /server function.`,
        );
      }
      return v;
    },
    async put(name: string, value: string) {
      checkName(name);
      const s3 = await cfg.getClient();
      await s3.send(
        new PutObjectCommand({
          Bucket: cfg.bucket,
          Key: `${cfg.secretsPrefix}${name}`,
          Body: new TextEncoder().encode(value),
          ContentType: "text/plain; charset=utf-8",
          ...(cfg.kmsKeyArn
            ? { ServerSideEncryption: "aws:kms", SSEKMSKeyId: cfg.kmsKeyArn }
            : {}),
        }),
      );
    },
    async list() {
      const s3 = await cfg.getClient();
      const out: SecretEntry[] = [];
      let token: string | undefined;
      do {
        const res = await s3.send(
          new ListObjectsV2Command({
            Bucket: cfg.bucket,
            Prefix: cfg.secretsPrefix,
            ContinuationToken: token,
          }),
        );
        for (const o of res.Contents ?? []) {
          if (!o.Key) continue;
          const name = o.Key.slice(cfg.secretsPrefix.length);
          if (!name) continue;
          out.push({ name, size: o.Size ?? 0, lastModified: o.LastModified?.toISOString() });
        }
        token = res.NextContinuationToken;
      } while (token);
      return out;
    },
  };
}
